'use client';

import { MouseEvent, useState } from 'react';
import { WithClassName } from 'src/utils/UtilityTypes';
import { InsuranceFundListData } from './types';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { cm } from 'src/utils/classNames';
import { Details, Swap } from '@drift-labs/icons';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import CardActionButtons from 'src/components/Vaults/InsuranceFundVaults/CardActionButtons';
import { CardAssetBalance } from 'src/components/Vaults/InsuranceFundVaults/CardAssetBalance';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import { CardWarning } from '../CardWarning';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { getCleanedMarketSymbol } from 'src/utils/market';
import NumLib from 'src/utils/NumLib';
import Button from 'src/components/primitives/Button/Button';
import { PageRoute } from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { BalanceDisplayType } from 'src/@types/types';
import IconButton from 'src/components/primitives/IconButton/IconButton';

interface MobileTableCardsProps extends WithClassName {
	data: InsuranceFundListData[];
	isPortfolioPage?: boolean;
}

interface MobileTableCardProps {
	row: InsuranceFundListData;
	isPortfolioPage?: boolean;
}

const MobileTableCard = ({ row, isPortfolioPage }: MobileTableCardProps) => {
	const spotMarkets = useSpotMarkets();
	const spotMarketConfig = spotMarkets[row.marketIndex];
	const isUsdc = spotMarketConfig?.symbol === 'USDC';
	const [displayType, setDisplayType] = useState<BalanceDisplayType>('base');

	const yourShare =
		row.vaultBalance > 0 ? (row.yourBalanceQuote / row.vaultBalance) * 100 : 0;

	const setDrawerStore = useDrawersStore((s) => s.set);

	const handleViewDetails = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		setDrawerStore((s) => {
			s.insuranceFundVault.show = true;
			s.insuranceFundVault.marketIndex = row.marketIndex;
		});
	};

	const apyToShow =
		row.apy > 0 && row.apy < 0.01
			? '< 0.01'
			: !isFinite(row.apy) || row.apy > 10000
				? '> 10,000'
				: row.apy.toFixed(2);

	if (!spotMarketConfig) {
		return null;
	}

	if (!spotMarketConfig) {
		return null;
	}

	const decimals = isUsdc ? 2 : spotMarketConfig.precisionExp.toNumber();

	const userBalanceDisplay = (() => {
		if (!row.yourBalanceBase) {
			return '-';
		}

		if (displayType === 'base') {
			return (
				<>
					<MarketIcon
						marketSymbol={getCleanedMarketSymbol(spotMarketConfig.symbol)}
						sizeClass="size-4"
					/>
					{row.yourBalanceBase.toLocaleString(Env.locale, {
						minimumFractionDigits: decimals,
						maximumFractionDigits: decimals,
					})}
				</>
			);
		}

		return BigNum.fromPrint(
			`${row.yourBalanceQuote}`,
			QUOTE_PRECISION_EXP
		).toNotional();
	})();

	const userEarningsDisplay = (() => {
		if (!row.cumulativeEarningsBase) {
			return '-';
		}

		if (displayType === 'base') {
			return (
				<>
					<MarketIcon
						marketSymbol={getCleanedMarketSymbol(spotMarketConfig.symbol)}
						sizeClass="size-4"
					/>
					{row.cumulativeEarningsBase.toLocaleString(Env.locale, {
						minimumFractionDigits: decimals,
						maximumFractionDigits: decimals,
					})}
				</>
			);
		}

		return BigNum.fromPrint(
			`${row.cumulativeEarningsQuote}`,
			QUOTE_PRECISION_EXP
		).toNotional();
	})();

	return (
		<div
			className={cm(
				'flex w-full flex-col gap-y-lg rounded-sm bg-container-bg p-md'
			)}
		>
			<div className="flex items-center gap-x-md">
				<MarketIcon
					marketSymbol={getCleanedMarketSymbol(spotMarketConfig.symbol)}
					sizeClass="size-8"
				/>
				<span className="typo-t2 text-text-default">
					{getCleanedMarketSymbol(spotMarketConfig.symbol)} Vault
				</span>
			</div>
			<div className="grid grid-cols-2 gap-y-md">
				{!isPortfolioPage && (
					<>
						<div className="flex flex-col gap-y-xs">
							<span className="typo-b4 text-text-secondary">Vault Balance</span>
							<span className="flex items-center gap-x-xs typo-t4 text-text-default">
								<MarketIcon
									marketSymbol={getCleanedMarketSymbol(spotMarketConfig.symbol)}
									sizeClass="size-4"
								/>
								{NumLib.millify(row.vaultBalance).displayString}
							</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<span className="typo-b4 text-text-secondary">APY</span>
							<span
								className={cm(
									'typo-t4',
									row.apy < 0
										? 'text-text-negative-red-button'
										: 'text-text-positive-green-button'
								)}
							>
								{`${apyToShow}%`}
							</span>
						</div>
					</>
				)}
				<div className="flex flex-col gap-y-xs">
					<span className="flex items-center gap-x-xs typo-b4 text-text-secondary">
						Your Balance
						<IconButton
							size="sm"
							variant="tertiary"
							onClick={() =>
								setDisplayType((curr) =>
									curr === 'base' ? 'notional' : 'base'
								)
							}
							icon={Swap}
						/>
					</span>
					<span className="flex items-center gap-x-xs typo-t4 text-text-default">
						{userBalanceDisplay}
					</span>
				</div>
				<div className="flex flex-col items-end gap-y-xs">
					<span className="flex items-center gap-x-xs typo-b4 text-text-secondary">
						Your Earnings
						<IconButton
							size="sm"
							variant="tertiary"
							onClick={() =>
								setDisplayType((curr) =>
									curr === 'base' ? 'notional' : 'base'
								)
							}
							icon={Swap}
						/>
					</span>
					<span className="flex items-center gap-x-xs typo-t4 text-text-default">
						{userEarningsDisplay}
					</span>
				</div>
				{!isPortfolioPage && (
					<>
						<div className="flex flex-col gap-y-xs">
							<span className="typo-b4 text-text-secondary">Your Share</span>
							<span className="flex items-center gap-x-xs typo-t4 text-text-default">
								{`${yourShare.toFixed(2)}%`}
							</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<span className="typo-b4 text-text-secondary">
								Vault Performance
							</span>
							<span className="typo-t4 text-text-default">
								<TextButton onClick={handleViewDetails} rightIcon={Details}>
									View
								</TextButton>
							</span>
						</div>
					</>
				)}
			</div>
			{/* If we're on the portfolio page, simply offer a link to the insurance fund vaults page. */}
			{isPortfolioPage && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					as="a"
					href={`/${PageRoute.vaults}/insurance-fund-vaults`}
				>
					Manage
				</Button>
			)}

			{!isPortfolioPage && (
				<>
					<CardWarning ifData={row.ifData} spotMarket={spotMarketConfig} />
					<CardActionButtons
						ifData={row.ifData}
						spotMarket={spotMarketConfig}
					/>
					<div className="flex justify-center">
						<CardAssetBalance marketIndex={row.marketIndex} />
					</div>
				</>
			)}
		</div>
	);
};

export const MobileTableCards = ({
	className,
	data,
	isPortfolioPage = false,
}: MobileTableCardsProps) => {
	return (
		<div
			className={cm(
				'flex w-full flex-col items-center gap-y-md pb-2xl',
				className
			)}
		>
			{data.map((row) => (
				<MobileTableCard
					key={`${row.marketIndex}-${row.poolId}`}
					row={row}
					isPortfolioPage={isPortfolioPage}
				/>
			))}
		</div>
	);
};
